import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "../../DashboardLayout";
import { Button, Form } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { fetchRetentionRate } from "../../../services/axios/apiManager";
import { useNavigate } from "react-router-dom";
import { handleErrors } from "../../../services/dataManager";
import LoadingSpinner from "../../../Components/spinner";
import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import BSPagination from "../../../Components/pagination";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

function RetentionAdFrequency() {
  const tableRef = useRef(null);
  const navigate = useNavigate();
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState({});

  const getRetentionRate = async (
    rng,
    page = pagination?.currentPage || 1,
    pageSize = pagination?.pageSize || 10
  ) => {
    setFetching(true);
    const res = await fetchRetentionRate(rng, page, pageSize);
    if (res?.data?.success && res?.data?.data) {
      setTableData(res.data.data);
      setPagination(res?.data?.metadata || {});
    } else {
      handleErrors(res, navigate);
    }
    setFetching(false);
    setLoading(false);
  };

  const handlePageChange = async (page) => {
    handleReset();
    await getRetentionRate(range, page, pagination.pageSize || 10);
  };

  const handleFilterChange = async (e) => {
    setRange(e.target.value);
    await getRetentionRate(e.target.value);
  };

  const handleReset = async () => {
    setRange(1);
    await getRetentionRate(1);
  };

  const chartData = {
    labels: tableData.map(
      (item) => `${item.first_name || ""} ${item.last_name || ""}`
    ),
    datasets: [
      {
        label: "Retention Rate (%)",
        data: tableData.map((item) => item.visit_percentage),
        backgroundColor: "rgba(255, 159, 64, 0.6)",
      },
      {
        label: "Avg. Ads Watched",
        data: tableData.map((item) => item.total_ads_watched),
        backgroundColor: "rgba(54, 162, 235, 0.6)",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { labels: { color: "#ffffff" } },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            ctx.dataset.label === "Retention Rate (%)"
              ? `${ctx.parsed.y}%`
              : `${ctx.parsed.y} ads`,
        },
      },
    },
    scales: {
      x: { ticks: { color: "#ffffff" } },
      y: {
        beginAtZero: true,
        ticks: { color: "#ffffff" },
      },
    },
  };
  useEffect(() => {
    getRetentionRate();
  }, []);

  useEffect(() => {
    const $table = $(tableRef.current);
    const rows = tableData.map((item) => {
      return [
        `${item.first_name || ""} ${item.last_name || ""}`,
        item.total_ads_watched,
        item.number_of_days_returned,
        item.visit_percentage,
      ];
    });

    const config = {
      data: rows,
      columns: [
        { title: "User" },
        { title: "Ads Watched" },
        { title: "Days Returned" },
        { title: "Retention Rate" },
      ],
      destroy: true,
      responsive: true,
      paging: false,
      searching: false,
    };

    if ($.fn.dataTable.isDataTable(tableRef.current)) {
      $table.DataTable().clear().rows.add(rows).draw();
    } else {
      $table.DataTable(config);
    }
    return () => {
      if ($.fn.dataTable.isDataTable(tableRef.current)) {
        $table.DataTable().destroy();
      }
    };
  }, [tableData]);

  return (
    <DashboardLayout>
      {/* Filter Card */}
      <div className="card bg-dark text-white mt-3">
        <div className="card-body">
          <h5 className="card-title">Filter by User Type</h5>
          <div className="row align-items-center mb-3">
            <div className="col-md-3">
              <Form.Select
                className="bg-dark text-light border-light"
                value={range}
                onChange={handleFilterChange}
              >
                <option value="1">1 Month</option>
                <option value="3">3 Month</option>
                <option value="6">6 Month</option>
              </Form.Select>
            </div>
            <div className="col-md-2">
              {fetching ? (
                <LoadingSpinner size="small" />
              ) : (
                <Button variant="success" onClick={handleReset}>
                  Reset
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="card bg-dark text-white mt-3">
        <div className="card-body">
          <h5 className="card-title">Retention & Ad Frequency</h5>
          <p className="card-text">
            Do users who watch more ads return more often?
          </p>
          <div className="table-responsive">
            <table
              ref={tableRef}
              className="table table-dark table-striped table-hover w-100"
            />
            {tableData.length === 0 && (
              <p className="text-center mt-3">No data available.</p>
            )}
          </div>
          <div className="container-fluid mt-2">
            <div className="d-flex justify-content-end">
              <div className="mx-4 py-2">
                <span className="text-light">{`Rows/Page ${pagination.pageSize || 0}, Total records ${pagination?.totalRecords || 0}`}</span>
              </div>
              <BSPagination
                currentPage={parseInt(pagination?.currentPage)}
                totalPages={parseInt(pagination?.totalPages)}
                onPageChange={handlePageChange}
                siblingCount={2}
                showFirstLast={true}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Chart Card */}
      <div className="card bg-dark text-white mt-3">
        <div className="card-body">
          <h6 className="mb-3">Retention vs. Ad Frequency by User Type</h6>
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>
    </DashboardLayout>
  );
}

export default RetentionAdFrequency;
